import React, { useMemo, useState, useLayoutEffect, useRef } from "react";
import { Worker, Viewer, ProgressBar } from "@react-pdf-viewer/core";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import useMessageDialog from "@Util/hook/useMessageDialog";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { v4 as uuid } from "uuid";

import { Box, Button, IconButton, Icons, Stack } from '@curves/ui-base';

import { isFunction } from "@Util/utils";
import usePrintApi from "@Apis/usePrintApi";
import { useSnackbar } from "notistack";
const PrevNextButtons = props => {
	const { isShowPrev = false, isShowNext = false, handlePrev, handleNext } = props;
	return (
		<React.Fragment>
			<Button variant="outlined" onClick={handlePrev} disabled={!isShowPrev} color="primary">
				上一份
			</Button>
			<Button variant="outlined" onClick={handleNext} disabled={!isShowNext} color="primary">
				下一份
			</Button>
		</React.Fragment>
	);
};
const ErrorView = props => {
	const { isError } = props;
	useLayoutEffect(
		() => {
			isError();
		},
		// eslint-disable-next-line
		[]
	);
	return (
		<Box className="pdf-doc-error">
			<Box className="pdf-doc-error-text">PDF檔案毀損，請重新整理</Box>
		</Box>
	);
};
const PdfComponent = React.forwardRef((props, ref) => {
	const { pdfUrl = "", onClose: onCloseProps = () => {}, onRefresh: onRefreshProps = null } = props;
	const setMessageDialog = useMessageDialog();
	const { enqueueSnackbar } = useSnackbar();
	const snackbarText = useRef('');
	const { postPrintApi } = usePrintApi();
	const zoomPluginInstance = zoomPlugin();
	const toolbarPluginInstance = toolbarPlugin();
	const { ZoomIn, ZoomOut, Zoom } = zoomPluginInstance;
	const { Toolbar } = toolbarPluginInstance;
	// 重刷畫面使用
	const [viewerKeyId, seViewKeyId] = useState(uuid());
	// 直接由 props 推導網址清單，首次渲染即有值，避免 Viewer 先拿到空網址
	const pdfUrls = useMemo(() => (Array.isArray(pdfUrl) ? pdfUrl : [pdfUrl]), [pdfUrl]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const currentPdfUrl = pdfUrls[currentIndex] || "";
	const hasError = useRef(false);
	const handleRefresh = () => {
		if (hasError.current) {
			setError(false);
			isFunction(onRefreshProps) &&
				onRefreshProps(() => {
					seViewKeyId(uuid());
				});
		}
	};
	const handlePrinter = () => {
		setMessageDialog({
			open: true,
			title: "列印PDF",
			msg: "是否要列印？",
			onConfirm: doPrintApi,
		});
	};
	const setError = isBool => {
		hasError.current = isBool;
	};
	const doPrintApi = async () => {
		await postPrintApi(currentPdfUrl);
	};
	const showSnackbarText = (text) => {
		if (text === snackbarText.current) return;
		enqueueSnackbar(text, {
			variant: "default",
			anchorOrigin: { vertical: "bottom", horizontal: "center" },
			autoHideDuration: 1000
		});
		snackbarText.current = text;
	}
	return (
		<Worker workerUrl={"https://s.curves.com.tw/common/Scripts/pdf.worker.min.js"}>
			<Box
				className="rpv-core-viewer"
				style={{
					border: "1px solid rgba(0, 0, 0, 0.3)",
					display: "flex",
					flexDirection: "column",
					height: "100%",
				}}
			>
				<Box
					style={{
						alignItems: "center",
						backgroundColor: "#eeeeee",
						borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
						display: "flex",
						padding: "4px",
					}}
				>
					<Toolbar>
						{props => {
							const { Download, EnterFullScreen } = props;
							return (
								<React.Fragment>
									<Box style={{ padding: "0px 2px" }}>
										<Zoom>
											{({ onZoom }) => {
												return (
													<Stack spacing={1}>
														{pdfUrls.length > 1 && (
															<PrevNextButtons
																isShowPrev={currentIndex > 0}
																isShowNext={currentIndex < pdfUrls.length - 1}
																handlePrev={() => setCurrentIndex(currentIndex - 1)}
																handleNext={() => setCurrentIndex(currentIndex + 1)}
															/>
														)}
														<IconButton
															variant="outlined"
															onClick={() => {
																onZoom("ActualSize");
																showSnackbarText('原始大小');
															}}
														>
															<Icons.ResizeOriginal />
														</IconButton>
														<IconButton
															variant="outlined"
															onClick={() => {
																onZoom("PageFit");
																showSnackbarText("適當大小");
															}}
														>
															<Icons.ResizeFitToContainer />
														</IconButton>
														<IconButton
															variant="outlined"
															onClick={() => {
																onZoom("PageWidth");
																showSnackbarText('頁面寬度大小');
															}}
														>
															<Icons.ResizeFitToWidth />
														</IconButton>
													</Stack>
												);
											}}
										</Zoom>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										<ZoomOut>
											{({ onClick: onClickProps }) => {
												return (
													<IconButton onClick={() => {
														onClickProps();
														showSnackbarText('顯示縮小');
													}}>
														<Icons.MagnifyingGlassMinus />
													</IconButton>
												);
											}}
										</ZoomOut>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										<ZoomIn>
											{({ onClick: onClickProps }) => {
												return (
													<IconButton onClick={() => {
														onClickProps();
														showSnackbarText('顯示放大');
													}}>
														<Icons.MagnifyingGlassPlus />
													</IconButton>
												);
											}}
										</ZoomIn>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										{isFunction(onRefreshProps) && (
											<Button variant="outlined" onClick={handleRefresh} color="primary">
												重新整理
											</Button>
										)}
									</Box>
									<Box style={{ padding: "0px 2px", marginLeft: "auto" }}>
										<EnterFullScreen>
											{({ onClick: onClickProps }) => {
												return (
													<IconButton onClick={onClickProps}>
														<Icons.OpenInFull />
													</IconButton>
												);
											}}
										</EnterFullScreen>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										<Download>
											{({ onClick: onClickProps }) => {
												return (
													<IconButton onClick={onClickProps}>
														<Icons.Download />
													</IconButton>
												);
											}}
										</Download>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										<IconButton onClick={handlePrinter}>
											<Icons.Print />
										</IconButton>
									</Box>
									<Box style={{ padding: "0px 2px" }}>
										<IconButton onClick={onCloseProps}>
											<Icons.Close />
										</IconButton>
									</Box>
								</React.Fragment>
							);
						}}
					</Toolbar>
				</Box>
				<Box
					style={{
						flex: 1,
						overflow: "hidden",
						backgroundColor: "#ffffff",
					}}
				>
					{/* 空網址交給 pdf.js 會同步拋錯並讓整頁白屏，有網址才掛載 Viewer */}
					{currentPdfUrl && (
						<Viewer
							key={viewerKeyId}
							renderLoader={percentages => (
								<Box style={{ width: "240px" }}>
									<ProgressBar progress={Math.round(percentages)} />
								</Box>
							)}
							fileUrl={currentPdfUrl}
							plugins={[toolbarPluginInstance, zoomPluginInstance]}
							renderError={() => <ErrorView isError={() => setError(true)} />}
						/>
					)}
				</Box>
			</Box>
		</Worker>
	);
});
export default PdfComponent;
